import { useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import { Link } from 'react-router-dom'

interface IUseTimerNotificationsProps {
  timeRemaining: number
  status: 'idle' | 'running' | 'paused' | 'stopped'
  currentSubject: string | null
  onTimeExpired: () => void
}

/**
 * Hook para gerenciar notificações do timer
 * - Notificação browser quando tempo expira
 * - Toast quando tempo expira
 * - Solicita permissão de notificação ao iniciar
 */
export const useTimerNotifications = ({
  timeRemaining,
  status,
  currentSubject,
  onTimeExpired,
}: IUseTimerNotificationsProps) => {
  const hasExpiredRef = useRef(false)
  const hasRequestedPermissionRef = useRef(false)

  // Solicita permissão de notificação quando timer inicia
  useEffect(() => {
    if (status === 'running' && !hasRequestedPermissionRef.current) {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission()
        hasRequestedPermissionRef.current = true
      }
    }
  }, [status])

  // Reseta flag quando timer é resetado ou iniciado novamente
  useEffect(() => {
    if (status === 'idle' || status === 'stopped') {
      hasExpiredRef.current = false
    }
  }, [status])

  // Verifica se tempo expirou e dispara notificações
  useEffect(() => {
    if (status === 'running' && timeRemaining <= 0 && !hasExpiredRef.current && currentSubject) {
      hasExpiredRef.current = true

      // Toast notification com link para planejamento
      toast.success(
        ({ closeToast }) => (
          <div>
            <div style={{ marginBottom: '8px' }}>Sessão de {currentSubject} finalizada! ⏰</div>
            <Link
              to="/planejamento"
              onClick={closeToast}
              style={{
                color: '#fff',
                textDecoration: 'underline',
                fontWeight: 500,
                display: 'inline-block',
                marginTop: '4px',
              }}
            >
              Ir para Planejamento
            </Link>
          </div>
        ),
        {
          position: 'top-right',
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      )

      // Browser notification
      if ('Notification' in window && Notification.permission === 'granted') {
        const notification = new Notification('Sessão de Estudos Finalizada! ⏰', {
          body: `Parabéns! Você completou sua sessão de ${currentSubject}.`,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: 'timer-complete',
          requireInteraction: false,
        })

        // Auto close notification after 5 seconds
        setTimeout(() => {
          notification.close()
        }, 5000)

        // Handle notification click
        notification.onclick = () => {
          window.focus()
          notification.close()
        }
      }

      // Optional: Play sound (pode ser habilitado se desejar)
      // playNotificationSound()

      // Callback quando tempo expira
      onTimeExpired()
    }
  }, [timeRemaining, status, currentSubject, onTimeExpired])
}

// Optional: função para tocar som de notificação
// const playNotificationSound = () => {
//   const audio = new Audio('/notification.mp3')
//   audio.play().catch(err => console.log('Error playing sound:', err))
// }
